import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Truck, Shield, Award, Users, Heart, Package, Sparkles, Lock, Globe, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';

const ChiSiamoPage = () => {
  // Testimonianze realistiche
  const testimonials = [
    {
      name: 'Marco R.',
      location: 'Milano',
      rating: 5,
      text: 'Ordinato un orologio, arrivato in 3 giorni. Qualità ottima, molto meglio di quello che mi aspettavo. Ci tornerò sicuramente.',
      date: 'Gennaio 2026'
    },
    {
      name: 'Giulia T.',
      location: 'Roma',
      rating: 5,
      text: 'Ho preso una lampada per il soggiorno. Bellissima e ben imballata. Il servizio clienti mi ha risposto subito quando avevo una domanda.',
      date: 'Dicembre 2025'
    },
    {
      name: 'Alessandro M.',
      location: 'Torino',
      rating: 4,
      text: 'Spedizione veloce, prodotto conforme alle foto. Un po\' di attesa in più del previsto ma nulla di grave. Soddisfatto.',
      date: 'Febbraio 2026'
    },
    {
      name: 'Francesca L.',
      location: 'Napoli',
      rating: 5,
      text: 'Finalmente un sito italiano serio! Ho comprato regali di Natale per tutta la famiglia, tutto perfetto.',
      date: 'Dicembre 2025'
    },
    {
      name: 'Davide P.',
      location: 'Bologna',
      rating: 5,
      text: 'Prezzi onesti e prodotti di qualità. Ho già fatto 3 ordini, mai un problema. Lo consiglio.',
      date: 'Gennaio 2026'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="chi-siamo-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#e8f0e8] to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#1a5c3a] mb-4 block">
              La nostra storia
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1a3d2a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Chi <span className="italic">Siamo</span>
            </h1>
            <p className="text-xl text-[#4a7a5a] font-light max-w-2xl mx-auto">
              La rivoluzione dello shopping online, made in Italy
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement - IMPACTFUL */}
      <section className="py-16 md:py-20 bg-black text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="h-10 w-10 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="text-2xl md:text-4xl font-light mb-6 leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
              QANTRI nasce per <span className="italic text-[#D4AF37]">rivoluzionare</span> l&apos;idea di shopping online
            </h2>
            <p className="text-white/70 text-lg font-light max-w-2xl mx-auto">
              Non un semplice e-commerce, ma una piattaforma che integra <strong className="text-white">intelligenza artificiale avanzata</strong> per offrirti un&apos;esperienza d&apos;acquisto unica, personalizzata e sicura al 100%.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-neutral-100">
              <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                I <span className="italic">Fondatori</span>
              </h2>
              
              <p className="text-neutral-600 text-lg font-light leading-relaxed mb-6">
                QANTRI nasce dalla visione di due professionisti con competenze complementari:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-[#f8faf8] rounded-2xl p-6">
                  <h3 className="font-semibold text-[#1a3d2a] mb-2">Fabrizio</h3>
                  <p className="text-neutral-600 font-light">
                    Imprenditore con oltre <strong>15 anni di esperienza</strong> nel mondo del commercio e delle vendite. La sua conoscenza del mercato e dei clienti è il cuore strategico di QANTRI.
                  </p>
                </div>
                <div className="bg-[#f8faf8] rounded-2xl p-6">
                  <h3 className="font-semibold text-[#1a3d2a] mb-2">Giancarlo</h3>
                  <p className="text-neutral-600 font-light">
                    Esperto in <strong>comunicazione digitale e programmazione</strong>. La mente tecnologica dietro la piattaforma, responsabile dell&apos;integrazione delle tecnologie AI più avanzate.
                  </p>
                </div>
              </div>
              
              <p className="text-neutral-600 text-lg font-light leading-relaxed mb-6">
                Insieme abbiamo creato QANTRI perché crediamo che lo shopping online debba essere <strong>semplice, sicuro e di qualità</strong>. Niente prodotti scadenti, niente attese infinite, niente assistenza inesistente.
              </p>
              
              <p className="text-neutral-600 text-lg font-light leading-relaxed">
                La nostra missione è chiara: <strong>rendere lo shopping online un&apos;esperienza di cui fidarsi</strong>. Ogni prodotto è selezionato con cura, ogni ordine è tracciato, ogni cliente è assistito come merita.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Logistics & Trust Section */}
      <section className="py-16 md:py-20 bg-[#1a5c3a] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Globe className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Come <span className="italic">funzioniamo</span>
            </h2>
            <p className="text-white/70 text-lg font-light max-w-2xl mx-auto">
              Non produciamo i prodotti. Selezioniamo il meglio per te.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Package, 
                title: 'Magazzino in Italia', 
                desc: 'Disponiamo di un magazzino fisico in Italia per spedizioni rapidissime sui prodotti più richiesti.' 
              },
              { 
                icon: Globe, 
                title: 'Fornitori in Europa e nel mondo', 
                desc: 'Collaboriamo con fornitori selezionati in Europa e a livello globale per offrirti varietà e qualità.' 
              },
              { 
                icon: Truck, 
                title: 'Spedizioni sicure e tracciate', 
                desc: 'Ogni ordine è tracciabile dal momento della spedizione fino alla consegna a casa tua.' 
              },
              { 
                icon: Lock, 
                title: 'Pagamenti ultra-sicuri', 
                desc: 'Transazioni protette con crittografia avanzata. PayPal, carte di credito, tutti i metodi più sicuri.' 
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center"
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <item.icon className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-medium text-white mb-2">{item.title}</h3>
                <p className="text-sm text-white/70 font-light">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Technology Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#f8faf8] to-white rounded-3xl p-8 md:p-12 border border-neutral-100"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#1a5c3a]/10 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-[#1a5c3a]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Tecnologia <span className="italic">Intelligente</span>
              </h2>
            </div>
            
            <p className="text-neutral-600 text-lg font-light leading-relaxed mb-6">
              QANTRI integra <strong>piattaforme di intelligenza artificiale all&apos;avanguardia</strong> per migliorare ogni aspetto della tua esperienza:
            </p>
            
            <ul className="space-y-4 text-neutral-600 font-light">
              <li className="flex items-start gap-3">
                <span className="text-[#1a5c3a] font-bold">→</span>
                <span><strong>Ricerca intelligente</strong>: trova prodotti con voce o immagini</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1a5c3a] font-bold">→</span>
                <span><strong>Assistente AI</strong>: chatbot sempre disponibile per aiutarti</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1a5c3a] font-bold">→</span>
                <span><strong>Raccomandazioni personalizzate</strong>: prodotti selezionati in base ai tuoi gusti</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#1a5c3a] font-bold">→</span>
                <span><strong>Descrizioni ottimizzate</strong>: contenuti generati con AI per informarti meglio</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-20 bg-[#f8faf8]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Perché <span className="italic">sceglierci</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { 
                icon: Award, 
                title: '+2.300 Prodotti', 
                desc: 'Catalogo curato e selezionato' 
              },
              { 
                icon: Shield, 
                title: 'Garanzia Totale', 
                desc: 'Resi facili e rimborsi sicuri' 
              },
              { 
                icon: Truck, 
                title: 'Spedizione 24-48h', 
                desc: 'Consegna rapida in Italia' 
              },
              { 
                icon: Users, 
                title: 'Assistenza Italiana', 
                desc: 'Team pronto ad aiutarti' 
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#1a5c3a]/10 flex items-center justify-center">
                  <item.icon className="h-5 w-5 text-[#1a5c3a]" />
                </div>
                <h3 className="font-medium text-[#1a3d2a] mb-1 text-sm">{item.title}</h3>
                <p className="text-xs text-neutral-500 font-light">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Quote className="h-8 w-8 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Cosa dicono i nostri <span className="italic">clienti</span>
            </h2>
            <p className="text-neutral-500 font-light">
              Recensioni verificate dai nostri acquirenti
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((review, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#f8faf8] rounded-2xl p-6"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, starIndex) => (
                    <Star 
                      key={starIndex} 
                      className={`h-4 w-4 ${starIndex < review.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-neutral-300'}`} 
                    />
                  ))}
                </div>
                <p className="text-neutral-700 font-light mb-4 text-sm leading-relaxed">
                  &quot;{review.text}&quot;
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#1a3d2a] text-sm">{review.name}</p>
                    <p className="text-xs text-neutral-400">{review.location}</p>
                  </div>
                  <p className="text-xs text-neutral-400">{review.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-[#1a5c3a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Heart className="h-12 w-12 text-[#D4AF37] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Pronto a <span className="italic">scoprire</span> la differenza?
            </h2>
            <p className="text-white/80 text-lg font-light leading-relaxed max-w-2xl mx-auto mb-8">
              Unisciti a migliaia di clienti soddisfatti. Qualità, sicurezza e un servizio che ti ascolta davvero.
            </p>
            <Link to="/products">
              <Button className="h-14 px-8 text-sm uppercase tracking-[0.15em] font-medium bg-[#D4AF37] text-[#1a3d2a] hover:bg-[#e5c349] rounded-full transition-all">
                Esplora i prodotti
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Users className="h-10 w-10 text-[#1a5c3a] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-light text-[#1a3d2a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Hai domande?
            </h2>
            <p className="text-neutral-600 font-light mb-6">
              Il nostro team italiano è sempre disponibile per aiutarti.
            </p>
            <p className="text-[#1a5c3a] font-medium text-lg">
              assistenza@qantri.store
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamoPage;
